'use client'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { gql } from 'graphql-request'
import {
  Plus,
  School,
  Globe,
  AlertTriangle,
  LockIcon,
  FileText,
  Trash2,
  ChevronDown
} from 'lucide-react'
import { useForm, useFieldArray } from 'react-hook-form'

import { useMutation, useQuery } from '@hooks/graphql'
import { useCurrentUser } from '@contexts/currentUser'
import TextEditor from '@components/TextEditor'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogClose
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import MultipleFileInput from '@components/MultipleFileInput'
import Collapsible from '@components/Collapsible'

import { useSubscription } from '../../hooks/subscription'
import CreateTutorButton from './CreateTutorButton'

const CREATE_TUTOR_MUTATION = gql`
  mutation createTutor($input: CreateTutorInput!) {
    createTutor(input: $input) {
      tutor {
        id
        name
      }
    }
  }
`

const UPLOAD_DOCUMENTS_MUTATION = gql`
  mutation uploadDocuments($input: UploadDocumentsInput!) {
    uploadDocuments(input: $input) {
      files {
        url
      }
    }
  }
`

const TAGS_QUERY = gql`
  query tutorTags {
    tutorTags {
      id
      slug
      name
      color
    }
  }
`

const AI_MODELS_QUERY = gql`
  query aiModels {
    aiModels {
      model
      modelFriendly
      company
      vision
      selectable
    }
  }
`

const MAX_FILE_SIZE = 10000000 // 10MB

const NewTutorDialog = ({ open, onOpenChange }) => {
  const { verificationStatus } = useSubscription()
  const [fileSizeError, setFileSizeError] = useState(null)
  const navigate = useNavigate()
  const { hasProFeatures } = useSubscription()

  const form = useForm({
    defaultValues: {
      name: '',
      description: '',
      visibility: 'PRIVATE',
      socratic: false,
      stemModeEnabled: false,
      knowledgeBaseEnabled: false,
      suggestionsEnabled: false,
      documents: [],
      tagIds: [],
      model: 'default'
    },
    mode: 'onTouched'
  })

  const { control, handleSubmit, watch, trigger } = form

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documents'
  })

  const { mutateAsync: createTutor, isLoading: isCreating } = useMutation({
    gqlMutation: CREATE_TUTOR_MUTATION
  })

  const { mutateAsync: uploadDocuments, isLoading: isUploading } = useMutation({
    gqlMutation: UPLOAD_DOCUMENTS_MUTATION
  })

  const { data: { tutorTags: allTags = [] } = {} } = useQuery({
    queryKey: ['tutorTags'],
    gqlQuery: TAGS_QUERY
  })

  const { data: { aiModels = [] } = {} } = useQuery({
    queryKey: ['aiModels'],
    gqlQuery: AI_MODELS_QUERY
  })

  const { currentMembership } = useCurrentUser()
  const features = currentMembership?.organization?.features || []
  const tutorModelChoiceEnabled = features.includes('tutor_model_choice')

  const handleAddDocuments = event => {
    setFileSizeError(null)
    const uploadedFiles = Array.from(event.target.files)
    const oversizedFiles = uploadedFiles.filter(file => file.size > MAX_FILE_SIZE)

    if (oversizedFiles.length > 0) {
      setFileSizeError(`Files must be smaller than 10MB. Offending file(s): ${oversizedFiles.map(f => f.name).join(', ')}`)
      event.target.value = null
      return
    }

    const filesToAppend = uploadedFiles.map(file => ({ file }))
    append(filesToAppend)
    trigger('documents')
  }

  const onSubmit = async (formData) => {
    const { documents, ...tutorInput } = formData
    try {
      const data = await createTutor({ input: tutorInput })
      const { id } = data.createTutor.tutor

      if (tutorInput.knowledgeBaseEnabled && documents && documents.length > 0) {
        const filesToUpload = documents.map(doc => doc.file).filter(Boolean)
        if (filesToUpload.length > 0) {
          await uploadDocuments({ input: { id, files: filesToUpload } })
        }
      }

      navigate(`/educators/tutors/${id}`)
    } catch (error) {
      console.error('Failed to create tutor:', error)
    }
  }

  const knowledgeBaseEnabledInForm = watch('knowledgeBaseEnabled')
  const visibilityForm = watch('visibility')
  const isLoading = isCreating || isUploading

  const visibilityOptions = {
    PRIVATE: {
      icon: LockIcon,
      label: 'Private',
      description: 'Only available to you'
    },
    ORGANIZATION: {
      icon: School,
      label: 'Organization',
      description: 'Available to educators within your organization'
    },
    PUBLIC: {
      icon: Globe,
      label: 'Public',
      description: 'Publicly available online for others to discover'
    }
  }

  const CurrentVisibilityIcon = visibilityOptions[visibilityForm]?.icon

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          className='flex gap-2'
          id='new-tutor-button'
          aria-label='New tutor'
        >
          New tutor
          <Plus size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Create a new tutor</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
            <div className='space-y-5'>
              <FormField
                control={control}
                name='name'
                rules={{ required: 'Name is required' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder='Socratic Tutor' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name='description'
                rules={{ required: 'Prompt is required' }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prompt</FormLabel>
                    <FormControl>
                      <TextEditor.Container>
                        <TextEditor
                          className='h-[100px]'
                          id='description'
                          placeholder='Write the prompt for your tutor here...'
                          {...field}
                        />
                        <TextEditor.Actions>
                          <TextEditor.ToolbarToggle />
                        </TextEditor.Actions>
                      </TextEditor.Container>
                    </FormControl>
                    <FormDescription>
                      Describe a subject, lesson, or tutor style.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name='knowledgeBaseEnabled'
                rules={{ validate: value => !value || (value && fields.length > 0) || 'Upload documents to enable knowledge base' }}
                render={({ field }) => (
                  <FormItem className='flex flex-col'>
                    <div className='flex flex-row items-center justify-between'>
                      <div className='space-y-0.5 pr-4'>
                        <FormLabel>Knowledge base</FormLabel>
                        <FormDescription>
                          Upload documents to add custom knowledge.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </div>
                    <FormMessage className='pt-2' />
                  </FormItem>
                )}
              />

              {knowledgeBaseEnabledInForm && (
                <div className='mt-2'>
                  <FormItem>
                    <FormControl>
                      <MultipleFileInput
                        id='documents-upload'
                        accept='.pdf'
                        onChange={handleAddDocuments}
                        disabled={isLoading}
                        label='Upload documents'
                      />
                    </FormControl>
                    {fileSizeError && <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>{fileSizeError}</p>}
                    <FormDescription>
                      Files may be included in chat responses.
                    </FormDescription>
                  </FormItem>

                  {fields.length > 0 && (
                    <div className='flex flex-wrap gap-2'>
                      {fields.map((document, i) => (
                        <div key={document.id} className='flex items-center gap-2 px-2 py-1 text-sm border rounded-md bg-muted'>
                          <FileText className='h-4 w-4 text-muted-foreground' />
                          <span className='max-w-[150px] truncate' title={document.file.name}>{document.file.name}</span>
                          <Button
                            type='button'
                            variant='ghost'
                            size='icon'
                            className='h-5 w-5 text-muted-foreground hover:text-destructive'
                            onClick={() => remove(i)}
                            disabled={isLoading}
                            aria-label={`Remove ${document.file.name}`}
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <FormField
                control={control}
                name='socratic'
                render={({ field }) => (
                  <FormItem className='flex flex-row items-center justify-between'>
                    <div className='space-y-0.5 pr-4'>
                      <FormLabel>Socratic mode</FormLabel>
                      <FormDescription>
                        Turns on probing questions instead of direct answers.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name='stemModeEnabled'
                render={({ field }) => (
                  <FormItem className='flex flex-row items-center justify-between'>
                    <div className='space-y-0.5 pr-4'>
                      <FormLabel>STEM mode</FormLabel>
                      <FormDescription>
                        Accurate calculations & visualisations for STEM subjects.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name='suggestionsEnabled'
                render={({ field }) => (
                  <FormItem className='flex flex-row items-center justify-between'>
                    <div className='space-y-0.5 pr-4'>
                      <FormLabel>Suggested questions</FormLabel>
                      <FormDescription>
                        Suggests follow-up questions based on messages.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name='visibility'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Visibility</FormLabel>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant='outline' className='w-full sm:w-[350px] justify-between font-normal'>
                          <div className='flex items-center'>
                            {CurrentVisibilityIcon && <CurrentVisibilityIcon className='mr-2 h-4 w-4' />}
                            {visibilityOptions[field.value]?.label || 'Select visibility'}
                          </div>
                          <ChevronDown className='ml-2 h-4 w-4 opacity-50' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className='w-[350px]'>
                        {Object.entries(visibilityOptions).map(([value, { icon: Icon, label, description }]) => {
                          if (value === 'PUBLIC' && verificationStatus !== 'VERIFIED') {
                            return null
                          }
                          const isSelected = field.value === value
                          return (
                            <DropdownMenuItem
                              key={value}
                              onSelect={() => field.onChange(value)}
                              className={isSelected ? 'bg-accent text-accent-foreground' : ''}
                            >
                              <Icon className='mr-3 h-5 w-5 shrink-0' />
                              <div>
                                <p className='leading-tight font-medium'>{label}</p>
                                <p className='text-xs text-muted-foreground leading-tight'>{description}</p>
                              </div>
                            </DropdownMenuItem>
                          )
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {knowledgeBaseEnabledInForm && visibilityForm !== 'PRIVATE' && (
                <Alert variant='destructive'>
                  <AlertTriangle className='h-4 w-4' />
                  <AlertTitle>Heads up!</AlertTitle>
                  <AlertDescription>
                    Documents you upload will be visible to educators within {visibilityForm === 'ORGANIZATION' ? 'your organization' : 'the Mindjoy community'}.
                  </AlertDescription>
                </Alert>
              )}

              <Collapsible label='Advanced settings'>
                <div className='space-y-6 pt-4'>

                  <If condition={tutorModelChoiceEnabled}>
                    <FormField
                      control={control}
                      name='model'
                      render={({ field }) => {
                        const modelOption = aiModels.find(m => m.model === field.value)
                        return (
                          <FormItem>
                            <FormLabel>AI Model</FormLabel>
                            <FormControl>
                              <Select onValueChange={field.onChange} value={field.value || ''}>
                                <SelectTrigger id='' className='mt-1 w-[350px]'>
                                  <SelectValue placeholder='Select a model'>
                                    <Choose>
                                      <When condition={modelOption}>
                                        <div className='flex items-center'>
                                          <img src={`/ai_companies/${modelOption.company.toLowerCase()}.svg`} className='size-6 mr-3 shrink-0' />
                                          <p className='leading-none'>{modelOption.modelFriendly}</p>
                                        </div>
                                      </When>
                                      <Otherwise>Select a model</Otherwise>
                                    </Choose>
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <For each='modelOption' of={aiModels}>
                                    <If condition={modelOption.selectable || modelOption.value === field.value}>
                                      <SelectItem value={modelOption.model}>
                                        <div className='flex mt-1'>
                                          <img src={`/ai_companies/${modelOption.company.toLowerCase()}.svg`} className='size-6 mr-3 shrink-0' />
                                          <div>
                                            <p className='leading-none'>{modelOption.modelFriendly}</p>
                                            <small className='leading-none'>{modelOption.company}</small>
                                          </div>
                                        </div>
                                      </SelectItem>
                                    </If>
                                  </For>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormDescription>
                              Need help picking a model? Check our <a href='#' className='underline'>model guide</a>.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )
                      }}
                    />
                  </If>

                  <FormField
                    control={control}
                    name='summary'
                    rules={{ maxLength: { value: 200, message: 'Description can\'t be longer than 200 characters' } }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Short Description</FormLabel>
                        <FormControl>
                          <Textarea
                            className='h-[60px] resize-none bg-background dark:bg-background-dark ring-0 focus-visible:ring-0'
                            placeholder='Write a short description of your tutor (max 200 characters)'
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Visible when you share your tutor or assign it to a classroom.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name='tagIds'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags</FormLabel>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant='outline' className='w-full sm:w-[350px] justify-start font-normal'>
                              <span className='truncate'>
                                {field.value?.length > 0
                                  ? allTags.filter(tag => field.value.includes(tag.id)).map(tag => tag.name).join(', ')
                                  : 'Select tags'}
                              </span>
                              <ChevronDown className='ml-auto h-4 w-4 opacity-50' />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className='w-[350px]'>
                            {allTags.map(tag => (
                              <DropdownMenuCheckboxItem
                                key={tag.id}
                                checked={field.value?.includes(tag.id)}
                                onCheckedChange={checked => {
                                  const currentTags = field.value || []
                                  const newTags = checked
                                    ? [...currentTags, tag.id]
                                    : currentTags.filter(id => id !== tag.id)
                                  field.onChange(newTags)
                                }}
                                onSelect={e => e.preventDefault()}
                              >
                                {tag.name}
                              </DropdownMenuCheckboxItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <FormDescription>
                          Add tags to make your tutor more discoverable. Note: doesn't apply if visibility is set to private.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Collapsible>

            </div>

            <DialogFooter className='sm:justify-between pt-3'>
              <DialogClose asChild>
                <Button type='button' variant='outline'>Cancel</Button>
              </DialogClose>
              <CreateTutorButton
                hasProFeatures={hasProFeatures}
                loading={isCreating || isUploading}
              />
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default NewTutorDialog
